import {AutomationNode, NodeConnection} from 'crqlar-react-common/dist/models/automation/automation';

/**
 * Recipient pickers list dominators only; variable pickers list all ancestors (AUTOMATIONS_UI.md, Decisions).
 * An empty recipient reference fails the send ("No recipients found"); an empty variable only leaves a gap.
 */

const entryNodeIds = (nodes: AutomationNode[], connections: NodeConnection[]): string[] => {
    const withIncoming = new Set(connections.map(connection => connection.targetNodeId));
    return nodes.map(node => node.id).filter(id => !!id && !withIncoming.has(id));
};

const reachable = (
    startIds: string[],
    targetId: string,
    connections: NodeConnection[],
    without?: string
): boolean => {
    const frontier = startIds.filter(id => id !== without);
    const seen = new Set<string>(frontier);

    if (without) seen.add(without);

    while (frontier.length) {
        const nodeId = frontier.pop()!;
        if (nodeId === targetId) return true;

        connections
            .filter(connection => connection.sourceNodeId === nodeId)
            .forEach(connection => {
                if (seen.has(connection.targetNodeId)) return;
                seen.add(connection.targetNodeId);
                frontier.push(connection.targetNodeId);
            });
    }

    return false;
};

/**
 * The steps on every path to `nodeId`, keeping the input order (nearest first — Send Email seeds from it).
 * Falls back to all of `upstreamNodes` when the graph cannot answer (no id, no connections, unreachable step).
 */
export const guaranteedUpstreamNodes = (
    upstreamNodes: AutomationNode[],
    flowNodes: AutomationNode[] = [],
    connections: NodeConnection[] = [],
    nodeId?: string
): AutomationNode[] => {
    if (!nodeId || !flowNodes.length || !connections.length) return upstreamNodes;

    const entries = entryNodeIds(flowNodes, connections);
    if (!entries.length) return upstreamNodes;
    if (!reachable(entries, nodeId, connections)) return upstreamNodes;

    return upstreamNodes.filter(node => !!node.id && !reachable(entries, nodeId, connections, node.id));
};
